/**
 * ErrorBoundary — React error boundary для production stability.
 *
 * Без boundary'а любая uncaught error в render крашит весь tree —
 * игрок видит белый экран. С boundary'ом ловим, эмитим аналитику,
 * показываем fallback UI «что-то пошло не так, перезагрузи».
 *
 * Class component потому что componentDidCatch только в class API.
 * Functional analog `useErrorBoundary` появится когда React 19 stable.
 *
 * Wire: оборачиваем `<App />` в `main.tsx`. Любой child throw попадает
 * сюда. Reset через `location.reload()` — простой и надёжный путь
 * чтобы пересоздать всё state'ы заново.
 *
 * Privacy:
 *   - error.message + stack попадают в emit() — это технические данные
 *     не PII, OK для аналитики
 *   - НЕ показываем stack игроку (визуально некрасиво + privacy
 *     concerns если stack содержит file paths юзера)
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { emit } from '../telemetry/emit.js';

interface Props {
  readonly children: ReactNode;
}

interface State {
  readonly hasError: boolean;
  readonly errorMessage: string | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { hasError: false, errorMessage: null };

  static getDerivedStateFromError(error: unknown): State {
    const msg = error instanceof Error ? error.message : String(error);
    return { hasError: true, errorMessage: msg };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    const msg = error instanceof Error ? error.message : String(error);
    // componentStack — где в React tree случилось. Полезно для дебага
    // но содержит названия компонентов — не PII.
    const component = (info.componentStack ?? 'unknown')
      .split('\n')[1]
      ?.trim()
      .replace(/^in\s+/, '');
    emit('error.boundary_caught', {
      component: component ?? 'unknown',
      error_msg: msg.slice(0, 256), // cap length для D1 row size
    });
    // Также console.error чтобы dev видел в browser console.
    console.error('[ErrorBoundary]', error, info);
  }

  handleReload = (): void => {
    window.location.reload();
  };

  override render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '2rem',
            background: 'radial-gradient(circle at center, #1a1410 0%, #0a0805 100%)',
            color: 'rgba(255, 247, 196, 0.9)',
            fontFamily: 'var(--font-display, serif)',
            textAlign: 'center',
            gap: '1.2rem',
            zIndex: 999_999,
          }}
        >
          <p
            style={{
              fontSize: '1.4rem',
              color: 'rgba(242, 201, 92, 0.95)',
              letterSpacing: '0.04em',
              margin: 0,
            }}
          >
            что-то остановилось
          </p>
          <p
            style={{
              fontSize: '0.95rem',
              color: 'rgba(255, 247, 196, 0.7)',
              maxWidth: '24rem',
              lineHeight: 1.5,
              margin: 0,
            }}
          >
            Искра приостановилась. Перезагрузка вернёт к Роще — твой прогресс остался.
          </p>
          <button
            type="button"
            onClick={this.handleReload}
            style={{
              padding: '0.65rem 1.4rem',
              border: '1px solid rgba(242, 201, 92, 0.6)',
              borderRadius: '999px',
              background: 'rgba(242, 201, 92, 0.15)',
              color: 'rgba(255, 247, 196, 0.95)',
              fontFamily: 'inherit',
              fontSize: '0.9rem',
              letterSpacing: '0.06em',
              textTransform: 'lowercase',
              cursor: 'pointer',
            }}
          >
            перезагрузить
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
